import type { ReactNode, Ref } from 'react';
import {
  Button as AriaButton,
  ListBox,
  ListBoxItem,
  Popover,
  Select as AriaSelect,
  SelectValue,
  type SelectProps as AriaSelectProps,
} from 'react-aria-components';
import clsx from 'clsx';
import { FieldError } from './FieldError';
import { Label } from './Label';

export type SelectSize = 'sm' | 'md';

// Full class names, never built by concatenation, so Tailwind's scanner sees them.
const SIZE_CLASS: Record<SelectSize, string> = {
  sm: 'select-sm',
  md: '',
};

export interface SelectOption {
  id: string;
  label: string;
}

export interface SelectProps extends Omit<
  AriaSelectProps<SelectOption>,
  'className' | 'children' | 'items' | 'value' | 'onChange' | 'selectedKey' | 'onSelectionChange' | 'isInvalid'
> {
  options: readonly SelectOption[];
  value: string;
  onChange: (id: string) => void;
  /** A visible label above the trigger, laid out like `LabeledInput`'s; it names the select (otherwise use `aria-label`). */
  label?: ReactNode;
  /** Error message. A truthy value marks the field invalid and is announced as the trigger's description. */
  error?: ReactNode;
  /** `md` matches `Input`; `sm` is for compact chrome such as the header. */
  size?: SelectSize;
  className?: string;
  /** The trigger button, e.g. to return focus to it after a dialog closes. */
  ref?: Ref<HTMLButtonElement>;
}

/**
 * React Aria `Select` in daisyUI's `select` look: a button that opens a
 * listbox popover, with typeahead, arrow-key and Escape behaviour. Controlled
 * by option id. A long list scrolls inside the popover, which react-aria caps
 * at the space left in the viewport. Name it with `label`, `aria-label` or `aria-labelledby`.
 * An `error` marks the root `data-invalid` (React Aria puts no `aria-invalid` on a select's button).
 */
export function Select({
  options,
  value,
  onChange,
  label,
  size = 'md',
  error,
  className,
  ref,
  ...rest
}: SelectProps) {
  return (
    <AriaSelect
      {...rest}
      className={clsx(label && 'flex flex-col gap-1', className)}
      selectedKey={value}
      isInvalid={Boolean(error)}
      onSelectionChange={(key) => {
        if (key !== null) onChange(String(key));
      }}
    >
      {label ? <Label>{label}</Label> : null}
      <AriaButton
        ref={ref}
        className={clsx('select w-full', SIZE_CLASS[size], error ? 'select-error' : '')}
      >
        <SelectValue />
      </AriaButton>
      <FieldError>{error}</FieldError>
      <Popover className="flex min-w-(--trigger-width) flex-col rounded-box border border-base-300 bg-base-100 shadow-lg">
        <ListBox className="menu w-full min-h-0 flex-nowrap overflow-y-auto p-1 outline-none">
          {options.map((o) => (
            <ListBoxItem
              key={o.id}
              id={o.id}
              textValue={o.label}
              className="w-full cursor-pointer rounded-field px-3 py-1.5 outline-none data-[focused]:bg-base-200 data-[selected]:font-semibold"
            >
              {o.label}
            </ListBoxItem>
          ))}
        </ListBox>
      </Popover>
    </AriaSelect>
  );
}
